// Which works this viewer may be shown the "Find Readers" button on, for the
// REACT surfaces (the LISWP block hero and the analytics Papers table).
//
// The Slim surface — Works::FindReadersButtonComponent on the profile work
// cards — asks the same endpoint through the page's JSONP batcher,
// window.Academia.findReadersEligibilityFetcher. React deliberately does not
// reuse it:
//
//   - That batcher hands $.ajax a success handler and no error handler, and
//     clears its queue before the request leaves, so a dead batch is spelled
//     exactly like a slow one and every callback is dropped. The Slim component
//     can live with that — it ships hidden, so a dropped callback just means it
//     stays hidden — but a React component needs to know the request settled in
//     order to stop rendering a placeholder.
//   - It knows nothing about MAX_WORK_IDS. The analytics Papers table renders
//     every paper an author has, and a prolific author sails past the endpoint's
//     ceiling; the ids beyond it are dropped server-side, so those buttons would
//     never appear with no way to tell that from ineligibility.
//
// So this owns its own batching, split to the endpoint's ceiling, and fails
// closed on anything it cannot answer.

import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import routes from 'js/core_pre_webpack/routes';

// Set on <body> by a page that has decided this VIEWER may be offered the
// feature here at all — see FindReadersPageGate (Ruby) and
// Works::FindReadersButtonComponent::PAGE_GATE_CLASS, which is the canonical
// home of the string. Checking it client-side is what keeps the request below
// off the overwhelming majority of page views.
export const PAGE_GATE_CLASS = 'find-readers-enabled';

// Api::V0::Works::FindReadersEligibilityController::MAX_WORK_IDS. Ids past it
// are dropped rather than rejected, so batches are SPLIT here, never truncated.
const MAX_WORK_IDS = 200;

// Everything queued inside one window leaves as one request. Long enough for a
// table to mount every row and queue itself, short enough that the reveal still
// reads as immediate.
const BATCH_WINDOW_MS = 50;

type Resolver = (eligible: boolean) => void;

let pending = new Map<number, Resolver[]>();
let flushTimer: number | null = null;

export function pageOffersFindReaders(): boolean {
  return (
    typeof document !== 'undefined' &&
    document.body?.classList.contains(PAGE_GATE_CLASS) === true
  );
}

// The endpoint answers with ONLY the eligible ids, every one mapped to true, so
// a work missing from the response is a work whose button stays hidden. That
// asymmetry is the contract — see the controller — not an oversight.
async function requestChunk(workIds: number[]): Promise<Set<number>> {
  const response = await fetchWithCsrf(
    routes.api_v0_works_find_readers_eligibility_path({
      subdomain_param: 'api',
      work_ids: workIds.join(','),
    }),
  );
  if (!response.ok) {
    throw new Error(`Find Readers eligibility responded ${response.status}`);
  }

  const body = (await response.json()) as {
    find_readers_eligibility?: Record<string, unknown>;
  };

  return new Set(
    Object.keys(body.find_readers_eligibility ?? {})
      .map(Number)
      .filter((workId) => Number.isInteger(workId)),
  );
}

function answer(
  batch: Map<number, Resolver[]>,
  workIds: number[],
  eligible: Set<number>,
): void {
  workIds.forEach((workId) => {
    batch.get(workId)?.forEach((resolve) => resolve(eligible.has(workId)));
  });
}

function flush(): void {
  flushTimer = null;

  // Swapped before the requests leave, so anything queued while they are in
  // flight starts a new batch rather than joining one that has already gone.
  const batch = pending;
  pending = new Map();

  const workIds = Array.from(batch.keys());

  for (let start = 0; start < workIds.length; start += MAX_WORK_IDS) {
    const chunk = workIds.slice(start, start + MAX_WORK_IDS);

    void requestChunk(chunk)
      .then((eligible) => answer(batch, chunk, eligible))
      // Fails CLOSED, and silently. An unanswerable work is treated exactly as
      // an ineligible one, because the alternative — a button that cannot do
      // anything — costs the author a click and a page load to discover.
      .catch(() => answer(batch, chunk, new Set()));
  }
}

/**
 * Whether this viewer may be shown the button on this work. Resolves false
 * rather than rejecting, for anything from an ineligible paper to a dead
 * request.
 */
export function isEligible(workId: number): Promise<boolean> {
  return new Promise((resolve) => {
    const resolvers = pending.get(workId);
    if (resolvers) {
      resolvers.push(resolve);
    } else {
      pending.set(workId, [resolve]);
    }

    flushTimer ??= window.setTimeout(flush, BATCH_WINDOW_MS);
  });
}
